import React, { useMemo } from "react";
import { toast } from "react-toastify";
import {
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconHeart,
  IconMessageCircle,
  IconBookmark,
  IconBookmarkFilled,
} from "@tabler/icons-react";
import MentionAutocomplete from "../../../../../components/MentionAutocomplete";
import Button from "../../../../../components/ui/Button";
import ReplyBox from "../../ReplyBox/ReplyBox";
import AvatarById from "../../../../../components/common/AvatarById";
import ActionButton from "../../../../../components/common/ActionButton.jsx";
import MentionText from "../../../../../components/common/MentionText";
import { formatRelativeTime } from "../../../../../hooks/useRelativeTime";
import useUserProfile from "../../../../../hooks/useUserProfile.js";
import { getDisplayName } from "../../../../../utils/userDisplay";
import { stripStructuredMentions } from "../../../../../utils/mentionInjection.js";

const CommentAuthor = React.memo(({ comment }) => {
  const { profile } = useUserProfile(comment.userId);
  const display = useMemo(() => {
    const name = getDisplayName({ profile, candidates: [comment?.username] });
    const profession =
      profile?.userProfession ||
      profile?.profession ||
      comment.profession ||
      "";
    const photoURL =
      profile?.photoURL ||
      profile?.userPhotoURL ||
      comment.photoURL ||
      comment.userPhotoURL ||
      "";
    return { name, profession, photoURL };
  }, [
    profile,
    comment.username,
    comment.profession,
    comment.photoURL,
    comment.userPhotoURL,
  ]);

  return (
    <div className="comment-user-info">
      <AvatarById
        userId={comment.userId}
        fallbackUsername={comment?.username || "User"}
        size="medium"
        className="comment-avatar avatar-md"
      />
      <div className="user-info">
        <h4>{display.name}</h4>
        <p className="comment-profession">{display.profession}</p>
      </div>
    </div>
  );
});

const CommentItem = ({
  comment,
  postId,
  isEditing,
  editedText,
  showOptions,
  showReplyInput,
  replyToUser,
  savedSet,
  commentLikePending,
  likedCommentIds,
  COMMENT_MAX_LENGTH,
  optionsMenuRef,
  isCommentOwner,
  setShowOptions,
  setIsEditing,
  setEditedText,
  handleDeleteClick,
  handleCommentEdit,
  handleMentionSelect,
  mentionMap,
  handleCommentLike,
  toggleReplies,
  handleToggleSaveComment,
}) => {
  return (
    <div>
      <div className={`comment-box ${comment.isOptimistic ? "optimistic" : ""}`}>
        <div className="comment-header">
          <div className="comment-user">
            <CommentAuthor comment={comment} />
          </div>
          <div className="comment-meta">
            <span className="comment-timestamp">
              {formatRelativeTime(comment.timestamp)}
              {comment.updatedAt && (
                <span
                  className="edited-badge"
                  title={`Edited ${formatRelativeTime(comment.updatedAt)}`}
                >
                  · edited
                </span>
              )}
            </span>
            <div
              className="comment-options"
              ref={showOptions === comment.id ? optionsMenuRef : null}
            >
              {isCommentOwner(comment) && (
                <button
                  className="options-toggle"
                  onClick={() =>
                    setShowOptions(
                      showOptions === comment.id ? null : comment.id,
                    )
                  }
                  aria-label="Comment options"
                  aria-haspopup="menu"
                  aria-expanded={showOptions === comment.id}
                >
                  <IconDotsVertical size={16} />
                </button>
              )}
              {showOptions === comment.id && isCommentOwner(comment) && (
                <div
                  className="options-menu"
                  role="menu"
                  aria-label="Comment actions"
                >
                  <button
                    role="menuitem"
                    onClick={() => {
                      const { plainText, mentionMap: parsedMentions } = stripStructuredMentions(comment.text);
                      setIsEditing(comment.id);
                      setEditedText(plainText);
                      // Prime the parent's mentionMap state for this session
                      Object.entries(parsedMentions).forEach(([username, id]) => {
                        handleMentionSelect({ username, id });
                      });
                      setShowOptions(null);
                    }}
                  >
                    <IconEdit size={16} />
                    Edit
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => handleDeleteClick(comment.id)}
                  >
                    <IconTrash size={16} />
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="comment-content">
          {isEditing === comment.id ? (
            <div className="comment-edit-container">
              <MentionAutocomplete
                value={editedText}
                onChange={(newValue) => setEditedText(newValue)}
                onMentionSelect={handleMentionSelect}
                mentionNames={mentionMap ? Object.keys(mentionMap) : []}
                className="comment-edit-input form-textarea"
                maxLength={COMMENT_MAX_LENGTH}
                placeholder="Edit comment…"
                aria-label="Edit comment"
              />
              <div className="edit-buttons">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleCommentEdit(comment.id)}
                  disabled={!editedText.trim()}
                >
                  Save
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setIsEditing(null);
                    setEditedText("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <p>
              <MentionText text={comment.text} />
            </p>
          )}
        </div>

        <div className="comment-actions">
          <ActionButton
            onClick={() => handleCommentLike(comment.id, comment)}
            icon={
              <IconHeart
                size={20}
                fill={!!likedCommentIds[comment.id] ? "#31C65B" : "none"}
                stroke={!!likedCommentIds[comment.id] ? "#31C65B" : "currentColor"}
              />
            }
            text="Like"
            count={comment.likes}
            active={!!likedCommentIds[comment.id]}
            disabled={
              !!commentLikePending[comment.id] ||
              (typeof comment.id === "string" && comment.id.startsWith("temp-")) ||
              !!comment.isOptimistic
            }
            className={`comment-like-btn ${!!likedCommentIds[comment.id] ? "liked" : ""}`}
            title={
              commentLikePending[comment.id] ? "Please wait…" : undefined
            }
          />
          <ActionButton
            onClick={() => {
              if (typeof comment.id === "string" && comment.id.startsWith("temp-")) {
                toast.info("Comment still being saved — try again in a moment");
                return;
              }
              toggleReplies(comment.id, { userId: comment.userId, name: comment.username });
            }}
            icon={<IconMessageCircle size={20} />}
            text="Reply"
            count={comment.replyCount || 0}
            active={showReplyInput === comment.id}
            className={`comment-reply-btn ${showReplyInput === comment.id ? "active" : ""}`}
          />
          <ActionButton
            onClick={() => handleToggleSaveComment(comment)}
            icon={
              savedSet.has(comment.id) ? (
                <IconBookmarkFilled size={20} />
              ) : (
                <IconBookmark size={20} />
              )
            }
            text="Save"
            className={`comment-save-btn ${savedSet.has(comment.id) ? "saved" : ""}`}
            title={
              savedSet.has(comment.id) ? "Unsave comment" : "Save comment"
            }
          />
        </div>

        {showReplyInput === comment.id && (
          <ReplyBox
            postId={postId}
            commentId={comment.id}
            isVisible={true}
            replyToUser={replyToUser}
          />
        )}
      </div>
    </div>
  );
};

export default CommentItem;
